<!--
  SupplierHeadlineNumbers — the three live club numbers a prospective supplier
  reads: open member orders, products members are looking for, club members.

  Lifted out of UnderReviewScreen (supplier-messages P2-A) when it gained two more
  callers: the club picker's supplier card and the gains page. GET
  /suppliers-club/headline — any signed-in user, three club-wide integers, cached
  10 minutes server-side (brief 2026-10-18-supplier-showroom), read once per mount.
  A hand-built JSON:API document, so `apiClient` reads it raw (like
  /clubs/poolshare-total). Skeleton while loading; the whole card is hidden on
  error or a malformed body — never a blank card.

  Labels: supplier.waiting.headline.* (owner-approved, design Section 13).
-->
<template>
  <div
    v-if="state !== 'error'"
    class="supplier-headline-numbers"
    role="group"
    :aria-label="$t('supplier.waiting.headlineAria')"
    :aria-busy="state === 'loading'"
  >
    <div
      v-for="key in HEADLINE_KEYS"
      :key="key"
      class="supplier-headline-numbers__cell"
      :data-key="key"
    >
      <span
        v-if="state === 'ready'"
        class="supplier-headline-numbers__value"
      >{{ formatCount(values[key]) }}</span>
      <span
        v-else
        class="supplier-headline-numbers__value skeleton-text skeleton-effect-wave"
      >000</span>
      <span class="supplier-headline-numbers__label">{{ $t(`supplier.waiting.headline.${key}`) }}</span>
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref } from 'vue';
import { apiClient } from '@/httpConfig/config/axios.js';

defineOptions({
  name: 'SupplierHeadlineNumbers',
});

const HEADLINE_KEYS = ['openDemandLines', 'missingProducts', 'members'];

const state = ref('idle');
const values = ref(null);

async function load() {
  state.value = 'loading';
  try {
    const response = await apiClient.get('/suppliers-club/headline');
    const attributes = response?.data?.data?.attributes ?? {};
    const next = Object.fromEntries(HEADLINE_KEYS.map((key) => [key, Number(attributes[key])]));
    if (HEADLINE_KEYS.some((key) => !Number.isFinite(next[key]))) throw new Error('headline shape');
    values.value = next;
    state.value = 'ready';
  } catch (err) {
    console.warn('[SupplierHeadlineNumbers] headline unavailable, card hidden:', err?.message);
    state.value = 'error';
  }
}

// Western digits always (owner rule), with grouping for a four-digit count.
const formatCount = (n) => n.toLocaleString('en-US');

onMounted(load);
</script>

<style scoped>
/* One row of three cells (canvas Waiting / Gains / Main). */
.supplier-headline-numbers {
  width: 100%;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
}

.supplier-headline-numbers__cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 12px 8px;
  border-radius: var(--poolit-radius-lg);
  background-color: var(--shn-cell-bg, #ffffff);
  border: 1px solid var(--poolit-border-color-light);
  text-align: center;
}

.supplier-headline-numbers__value {
  font-size: 24px;
  font-weight: 700;
  line-height: 1.2;
  color: var(--poolit-primary-600);
  font-variant-numeric: tabular-nums;
}

.supplier-headline-numbers__label {
  font-size: 11px;
  line-height: 1.35;
  color: var(--poolit-text-secondary);
}
</style>

<!--
  Non-scoped block: dark-mode overrides. Vue's :global() compound form is
  forbidden inside <style scoped> (CLAUDE.md) — F7 darkMode:'auto' adds `.dark`
  on <html>, so these rules live in a plain block.
-->
<style>
.dark .supplier-headline-numbers__cell {
  --shn-cell-bg: var(--poolit-bg-tertiary-dark);
  border-color: var(--poolit-border-color-on-dark);
}

.dark .supplier-headline-numbers__value {
  color: var(--poolit-primary-300);
}
</style>
